import React from "react";
import i18n from "i18next";
import { makeAction } from "../schema";

/* eslint-disable react/prop-types */

const TYPE_LABELS = {
  navigate: i18n.t("InteractiveScenario.ActionType.Navigate") || "Sahneye Git",
  set_element_visibility:
    i18n.t("InteractiveScenario.ActionType.SetElementVisibility") || "Element Göster/Gizle",
  alert: i18n.t("InteractiveScenario.ActionType.Alert") || "Bildirim",
  set_variable: i18n.t("InteractiveScenario.ActionType.SetVariable") || "Değişken Güncelle",
  end_scenario: i18n.t("InteractiveScenario.ActionType.EndScenario") || "Senaryoyu Bitir",
};

const OP_LABELS = {
  set: i18n.t("InteractiveScenario.ActionOperator.Set") || "Ata (=)",
  add: i18n.t("InteractiveScenario.ActionOperator.Add") || "Ekle (+=)",
  subtract: i18n.t("InteractiveScenario.ActionOperator.Subtract") || "Çıkar (-=)",
  toggle: i18n.t("InteractiveScenario.ActionOperator.Toggle") || "Toggle",
};

const VISIBILITY_LABELS = {
  show: i18n.t("InteractiveScenario.Visibility.Show") || "Göster",
  hide: i18n.t("InteractiveScenario.Visibility.Hide") || "Gizle",
  toggle: i18n.t("InteractiveScenario.Visibility.Toggle") || "Değiştir (toggle)",
};

const ELEMENT_TYPE_ICONS = {
  visualtext: "article",
  visualimage: "image",
  videocard: "videocam",
  flipcard: "flip",
  truefalse: "rule",
  mcq: "quiz",
};

export default function ActionEditor({
  action,
  scenes = [],
  variables = [],
  elements = [],
  onChange,
  onDelete,
}) {
  const set = (patch) => onChange({ ...action, ...patch });

  return (
    <div className="sce-action-editor">
      <div className="sce-action-editor__row">
        <select
          className="sce-action-editor__select"
          value={action.type}
          onChange={(e) =>
            set({
              type: e.target.value,
              targetSceneId: null,
              alertMessage: null,
              targetElementId: null,
              visibility: "show",
            })
          }
        >
          {Object.entries(TYPE_LABELS).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="sce-action-editor__delete"
          onClick={onDelete}
          title={i18n.t("InteractiveScenario.Action.DeleteAction") || "Aksiyonu sil"}
        >
          ✕
        </button>
      </div>

      {action.type === "navigate" && (
        <select
          className="sce-action-editor__select sce-action-editor__select--wide"
          value={action.targetSceneId || ""}
          onChange={(e) => set({ targetSceneId: e.target.value || null })}
        >
          <option value="">
            {i18n.t("InteractiveScenario.Option.SelectScene") || "— sahne seç —"}
          </option>
          {scenes.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title || s.id}
            </option>
          ))}
        </select>
      )}

      {action.type === "set_element_visibility" && (
        <>
          <select
            className="sce-action-editor__select sce-action-editor__select--wide"
            value={action.targetElementId || ""}
            onChange={(e) => set({ targetElementId: e.target.value || null })}
          >
            <option value="">
              {i18n.t("InteractiveScenario.Option.SelectElement") || "— element seç —"}
            </option>
            {elements.map((el) => (
              <option key={el.id} value={el.id}>
                {el.title || el.type}
              </option>
            ))}
          </select>
          <select
            className="sce-action-editor__select sce-action-editor__select--wide"
            value={action.visibility || "show"}
            onChange={(e) => set({ visibility: e.target.value })}
          >
            {Object.entries(VISIBILITY_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        </>
      )}

      {action.type === "alert" && (
        <input
          className="sce-action-editor__input"
          type="text"
          placeholder={i18n.t("InteractiveScenario.Placeholder.AlertMessage") || "Bildirim metni"}
          value={action.alertMessage || ""}
          onChange={(e) => set({ alertMessage: e.target.value })}
        />
      )}

      {action.type === "set_variable" && (
        <div className="sce-action-editor__var-row">
          <select
            className="sce-action-editor__select"
            value={action.variableName || ""}
            onChange={(e) => set({ variableName: e.target.value || null })}
          >
            <option value="">
              {i18n.t("InteractiveScenario.Option.SelectVariable") || "— değişken —"}
            </option>
            {variables.map((v) => (
              <option key={v.name} value={v.name}>
                {v.name}
              </option>
            ))}
          </select>
          <select
            className="sce-action-editor__select"
            value={action.variableOp || "set"}
            onChange={(e) => set({ variableOp: e.target.value })}
          >
            {Object.entries(OP_LABELS).map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
          <input
            className="sce-action-editor__input sce-action-editor__input--short"
            type="text"
            placeholder={i18n.t("InteractiveScenario.Placeholder.Value") || "Değer"}
            value={action.variableValue ?? ""}
            onChange={(e) => set({ variableValue: e.target.value })}
          />
        </div>
      )}
    </div>
  );
}

export function ActionListEditor({ actions = [], scenes, variables, elements, onChange }) {
  const add = () => onChange([...actions, makeAction()]);

  const update = (i, updated) => onChange(actions.map((a, idx) => (idx === i ? updated : a)));

  const remove = (i) => onChange(actions.filter((_, idx) => idx !== i));

  return (
    <div className="sce-action-list">
      {actions.map((action, i) => (
        <ActionEditor
          key={action.id}
          action={action}
          scenes={scenes}
          variables={variables}
          elements={elements}
          onChange={(updated) => update(i, updated)}
          onDelete={() => remove(i)}
        />
      ))}
      <button type="button" className="sce-add-btn" onClick={add}>
        {i18n.t("InteractiveScenario.Action.AddAction") || "+ Aksiyon Ekle"}
      </button>
    </div>
  );
}
